<template>
  <VcModal :title="title" modal-width="w-full max-w-[700px]" hide-actions is-mobile-fullscreen>
    <template #default="{ close }">
      <VcAddressForm
        :model-value="editableAddress"
        :countries="countriesWithoutEmbargoed"
        with-personal-info
        required-email
        required-city
        required-phone
        :hide-billing-address="isHidding"
        @save="saveAddress"
      >
        <template #append="{ dirty, valid }">
          <div class="flex w-full flex-col gap-4">
            <VcButton
              color="primary"
              variant="solid"
              :disabled="!dirty || !valid"
              :loading="loading"
              type="submit"
              size="lg"
            >
              {{ $t("common.buttons.save_address") }}
            </VcButton>

            <VcButton class="p-4" color="secondary" variant="outline" size="lg" @click="close">
              {{ $t("common.buttons.cancel") }}
            </VcButton>
          </div>
        </template>
      </VcAddressForm>
    </template>
  </VcModal>
</template>

<script setup lang="ts">
import { clone } from "lodash";
import { computed, onMounted, ref, watchEffect } from "vue";
import { useI18n } from "vue-i18n";
import { useCountriesWithoutEmbargoed } from "@/core/composables";
import type { MemberAddressTypeExtension } from "@/core/api/graphql/types";

const emit = defineEmits<IEmits>();

const props = defineProps<IProps>();

const isHidding = true;

interface IEmits {
  (event: "result", address: MemberAddressTypeExtension, isEdit: boolean): void;
}
interface IProps {
  address?: MemberAddressTypeExtension;
  loading?: boolean;
}

const { countriesWithoutEmbargoed, loadCountriesWithoutEmbargoed } = useCountriesWithoutEmbargoed();
const { t } = useI18n();
const editableAddress = ref<MemberAddressTypeExtension>();
const title = computed<string>(() =>
  editableAddress?.value?.countryCode
    ? t("pages.account.addresses.edit_address_title")
    : t("common.titles.new_address"),
);
const isEdit = computed<boolean>(() => title.value.includes("Edit"));

onMounted(async () => {
  if (!countriesWithoutEmbargoed.value.length) {
    await loadCountriesWithoutEmbargoed();
  }
});

watchEffect(() => {
  editableAddress.value = clone(props.address);
});

function saveAddress(address: MemberAddressTypeExtension) {
  emit("result", address, isEdit.value);
}
</script>

<style lang="scss" scoped>
.vc-button {
  @apply py-2.5 #{!important};
}

.vc-button--outline--secondary {
  @apply border-[--color-neutral-a3] border-[1px] rounded-md #{!important};
}
</style>
